import { Component, DestroyRef, HostListener, inject, signal, computed, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Commands } from '../../services/commands/commands';
import { Events } from '../../services/events/events';
import { Store } from '../../services/store/store';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-board',
  imports: [],
  templateUrl: './board.html',
  styleUrl: './board.css',
})
export class Board implements OnInit {

  // PROPERTIES

  private destroyRef = inject(DestroyRef);

  // SIGNALS

  protected phase = signal<'voting' | 'waiting_for_votes' | 'vote_result' | 'guess_result'>('voting');

  protected vote_data = signal<{
    voted_player_name: string;
    is_voted_player_impostor: boolean;
    is_game_ended: boolean;
    is_game_draw: boolean;
  } | null>(null);

  protected guess_data = signal<{
    guessed_word: string;
    is_word_correct: boolean;
    is_game_ended: boolean;
    is_game_draw: boolean;
  } | null>(null);

  protected is_word_visible = signal<boolean>(false);
  protected typed_word = signal<string>('');
  protected is_me_eliminated = signal<boolean>(false);

  protected local_player_name;
  protected remote_player_names;
  protected start_name;
  protected word;

  // COMPUTED SIGNALS

  protected is_vote_game_ended = computed<boolean>(() =>
    this.vote_data()?.is_game_ended ?? false
  );

  protected is_vote_game_draw = computed<boolean>(() => 
    this.vote_data()?.is_game_draw ?? false
  );
  
  protected is_voted_player_impostor = computed<boolean>(() =>
    this.vote_data()?.is_voted_player_impostor ?? false
  );
  
  protected voted_player_name = computed<string>(() =>
    this.vote_data()?.voted_player_name ?? ''
  );

  protected is_guess_game_ended = computed<boolean>(() =>
    this.guess_data()?.is_game_ended ?? false
  );

  protected is_guess_game_draw = computed<boolean>(() => 
    this.guess_data()?.is_game_draw ?? false
  );
  
  protected is_guess_correct = computed<boolean>(() =>
    this.guess_data()?.is_word_correct ?? false
  );
  
  protected guessed_word = computed<string>(() =>
    this.guess_data()?.guessed_word ?? ''
  );

  protected is_me_voted = computed<boolean>(() =>
    this.voted_player_name() === this.local_player_name()
  );

  // EVENT LISTENERS

  private initializeListeners(): void{

    this.events.onGameEnded()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: data => {

          // always react to disconnections, even if eliminated

          if (data.caused_by_disconnection){

            alert("The connection to a player is lost. The match has ended.");
            this.exitBoard();

          }

        }
      });

    this.events.onPlayerVoted()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: data => {

          if (this.is_me_eliminated()) return;

          this.vote_data.set(data);

          if (
            !this.is_voted_player_impostor() &&
            !this.is_vote_game_ended()
          ){

            this.is_me_voted()
              ? this.is_me_eliminated.set(true)
              : this.store.deleteRemotePlayer(this.voted_player_name());

          }

          this.phase.set('vote_result');

        }
      });

    this.events.onWordGuessed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: data => {

          if (this.is_me_eliminated()) return;

          this.guess_data.set(data);

          if (!this.is_guess_game_ended()){

            this.is_me_voted()
              ? this.is_me_eliminated.set(true)
              : this.store.deleteRemotePlayer(this.voted_player_name());

          }

          this.phase.set('guess_result');

        }
      });

  }

  // COMMANDS

  public vote(player_name: string): void{

    this.commands.votePlayer(player_name);
    this.phase.set('waiting_for_votes');

  }

  public submitWord(): void{

    const guessed_word = this.typed_word().trim().toLowerCase();
    this.commands.guessWord(guessed_word);

  }

  // UTILITIES

  public showWord(): void{
    this.is_word_visible.set(true);
  }

  public hideWord(): void{
    this.is_word_visible.set(false);
  }

  public onWordInput(event: Event): void{

    const target = event.target as HTMLInputElement;
    this.typed_word.set(target.value);

  }

  public continueRound(): void{

    this.phase.set('voting');
    this.vote_data.set(null);
    this.guess_data.set(null);
    this.is_word_visible.set(false);
    this.typed_word.set('');

  }

  public exitBoard(): void{

    const room_id = this.commands.getRoomId();
    this.router.navigate([`/impostor/waiting-room/${room_id}`]);

  }

  // HOST EVENTS

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent){

    if (!this.is_vote_game_ended() && !this.is_guess_game_ended())
      event.returnValue = true;

  }

  // LIFECYCLE

  constructor(
    private router: Router,
    private commands: Commands,
    private events: Events,
    private store: Store
  ){

    this.local_player_name = this.store.local_player_name;
    this.remote_player_names = this.store.remote_player_names;
    this.start_name = this.store.start_name;
    this.word = this.store.word;

  }

  ngOnInit(){
    this.initializeListeners();
  }

}
